import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Warning, ShieldWarning } from "@phosphor-icons/react";

export default function Report() {
  const [form, setForm] = useState({ name: "", email: "", category: "Suspicious behaviour", profile_ref: "", description: "" });
  const [sending, setSending] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/support/report", form);
      toast.success("Report received. A human reviewer will respond within one working day.");
      setForm({ name: "", email: "", category: "Suspicious behaviour", profile_ref: "", description: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-[900px] mx-auto px-6 md:px-10 py-16" data-testid="report-page">
      <div className="text-center">
        <ShieldWarning size={32} weight="duotone" className="text-brand-terracotta mx-auto" />
        <div className="label-eyebrow text-brand-terracotta mt-3">Trust</div>
        <h1 className="font-serif text-5xl md:text-6xl tracking-tighter mt-3 text-brand-ink">Report an issue.</h1>
        <p className="mt-4 text-brand-muted max-w-xl mx-auto leading-relaxed">
          Every report is read by a human within one working day. If the
          matter is safety-critical, please also call us on <a href="tel:+919892810623" className="text-brand-terracotta underline underline-offset-4">+91 98928 10623</a>.
        </p>
      </div>

      <div className="mt-10 bg-brand-gold/10 border border-brand-gold/30 p-5 flex gap-4 items-start">
        <Warning size={22} weight="fill" className="text-brand-gold mt-0.5 flex-shrink-0" />
        <p className="text-sm text-brand-ink/85 leading-relaxed">
          <strong>Emergency?</strong> If a member has requested money, sent
          explicit content, or coerced you in any way, please stop replying,
          take a screenshot if you can, and submit this form. We suspend
          concerned profiles within hours of a substantiated report.
        </p>
      </div>

      <form onSubmit={submit} className="mt-8 bg-white border border-brand-line p-8 md:p-10 space-y-5" data-testid="report-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <Label className="label-eyebrow text-brand-muted">Your name</Label>
            <Input required value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="report-name" className="mt-2 h-12 rounded-none border-brand-line" />
          </div>
          <div>
            <Label className="label-eyebrow text-brand-muted">Your email</Label>
            <Input type="email" required value={form.email} onChange={(e) => upd("email", e.target.value)} data-testid="report-email" className="mt-2 h-12 rounded-none border-brand-line" />
          </div>
          <div>
            <Label className="label-eyebrow text-brand-muted">Category</Label>
            <Select value={form.category} onValueChange={(v) => upd("category", v)}>
              <SelectTrigger data-testid="report-category" className="mt-2 h-12 rounded-none border-brand-line"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Suspicious behaviour">Suspicious behaviour</SelectItem>
                <SelectItem value="Financial request / scam">Financial request / scam</SelectItem>
                <SelectItem value="Inappropriate message">Inappropriate message</SelectItem>
                <SelectItem value="Fake or misleading profile">Fake or misleading profile</SelectItem>
                <SelectItem value="Harassment">Harassment</SelectItem>
                <SelectItem value="Payment issue">Payment issue</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-eyebrow text-brand-muted">Profile reference / URL (optional)</Label>
            <Input value={form.profile_ref} onChange={(e) => upd("profile_ref", e.target.value)} data-testid="report-profile" className="mt-2 h-12 rounded-none border-brand-line" placeholder="Profile ID or link" />
          </div>
        </div>
        <div>
          <Label className="label-eyebrow text-brand-muted">What happened?</Label>
          <Textarea required rows={7} value={form.description} onChange={(e) => upd("description", e.target.value)} data-testid="report-description" className="mt-2 rounded-none border-brand-line" placeholder="Describe the incident in as much detail as you can. Include dates and any relevant messages." />
        </div>
        <Button type="submit" disabled={sending} data-testid="report-submit" className="w-full h-13 rounded-full bg-brand-terracotta hover:bg-brand-terracotta/90 text-white">
          {sending ? "Sending…" : "Submit report"}
        </Button>
        <p className="text-[11px] text-brand-muted">Your report is confidential. The reported member is not told who reported them.</p>
      </form>
    </div>
  );
}
